import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Search, Loader2, Factory, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const IndustryComboBox = ({ token, value, onChange }) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [recent, setRecent] = useState([]);
  const ref = useRef(null);
  const debounceRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Sync external value
  useEffect(() => { setQuery(value || ''); }, [value]);

  const fetchSuggestions = useCallback(async (q) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/industries/suggest`, {
        params: { q }, headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestions(res.data.suggestions || []);
      setRecent(res.data.recent || []);
    } catch {
      setSuggestions([]);
    } finally { setLoading(false); }
  }, [token]);

  const handleInput = (val) => {
    setQuery(val);
    onChange(val);
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSelect = (item) => {
    setQuery(item);
    onChange(item);
    setOpen(false);
  };

  const handleFocus = () => {
    setOpen(true);
    if (suggestions.length === 0) fetchSuggestions(query);
  };

  const handleClear = () => {
    setQuery('');
    onChange('');
    setSuggestions([]);
  };

  return (
    <div ref={ref} className="relative" data-testid="industry-combo-box">
      <div className="relative">
        <Factory size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={query}
          onChange={e => handleInput(e.target.value)}
          onFocus={handleFocus}
          placeholder="e.g. Manufacturing, Healthcare, Financial Services..."
          data-testid="industry-input"
          className="w-full pl-10 pr-10 py-2.5 border border-slate-200 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
        />
        {loading ? (
          <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />
        ) : query ? (
          <button onClick={handleClear} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500" data-testid="industry-clear">
            <X size={16} />
          </button>
        ) : (
          <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300" />
        )}
      </div>

      {open && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-56 overflow-y-auto" data-testid="industry-dropdown">
          {recent.length > 0 && !query && (
            <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider bg-slate-50">
              Recently Used
            </div>
          )}
          {suggestions.map((item, i) => (
            <button key={`${item}-${i}`} onClick={() => handleSelect(item)}
              data-testid={`industry-option-${i}`}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 hover:text-blue-700 transition-colors ${
                item === query ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700'
              }`}>
              {item}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
